import React, { useState, useEffect } from 'react';
import axiosClient from '../api/axiosClient';
import { formatDate } from '../utils/dateUtils';

const ReportWhatsAppModal = ({ show = false, onClose, title = 'Report', reportData = {}, reportType = 'report' }) => {
  const [targetMobile, setTargetMobile] = useState('');
  const [sendingApi, setSendingApi] = useState(false);
  const [apiStatusMsg, setApiStatusMsg] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (reportData) {
      const mob = reportData.account?.MobileNo || reportData.CustomerMobile || reportData.SupplierMobile || '';
      setTargetMobile(mob || '');
      setCopied(false);
      setApiStatusMsg('');
    }
  }, [reportData, show]);

  if (!show || !reportData) return null;

  // Build formatted text message for WhatsApp
  const buildWhatsAppMessage = () => {
    const firmName = reportData.firmName || 'PUSHPAI SEA FOOD(ALIBAG)';
    const fromDate = formatDate(reportData.fromDate || new Date());
    const toDate = formatDate(reportData.toDate || new Date());

    if (reportType === 'statement') {
      const acc = reportData.account || {};
      const opBal = parseFloat(reportData.openingBalance || 0);
      const closeBal = parseFloat(reportData.closingBalance || 0);
      const totDebit = parseFloat(reportData.totalDebit || 0);
      const totCredit = parseFloat(reportData.totalCredit || 0);

      let msg = `📊 *ACCOUNT STATEMENT / LEDGER*\n`;
      msg += `🏢 *${firmName}*\n`;
      msg += `--------------------------------\n`;
      msg += `👤 *Party:* ${acc.AccountName || 'Customer'}\n`;
      if (acc.MobileNo) msg += `📞 *Mobile:* ${acc.MobileNo}\n`;
      msg += `📅 *Period:* ${fromDate} to ${toDate}\n`;
      msg += `--------------------------------\n`;
      msg += `• *Opening Balance:* ₹${opBal.toFixed(2)}\n`;
      msg += `• *Total Debits (Sales/Charges):* ₹${totDebit.toFixed(2)}\n`;
      msg += `• *Total Credits (Receipts/Payments):* ₹${totCredit.toFixed(2)}\n`;
      msg += `--------------------------------\n`;
      msg += `*NET CLOSING BALANCE:* *₹${closeBal.toFixed(2)}* (${closeBal >= 0 ? 'Receivable' : 'Payable'})\n`;
      msg += `--------------------------------\n`;
      msg += `🙏 _Thank you for your partnership!_`;
      return msg;
    }

    if (reportType === 'sales_report') {
      const totals = reportData.totals || {};
      let msg = `📈 *SALES SUMMARY REPORT*\n`;
      msg += `🏢 *${firmName}*\n`;
      msg += `📅 *Period:* ${fromDate} to ${toDate}\n`;
      msg += `--------------------------------\n`;
      msg += `• *Total Sales Value:* ₹${parseFloat(totals.totalGrand || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}\n`;
      msg += `• *Total Paid/Collected:* ₹${parseFloat(totals.totalPaid || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}\n`;
      msg += `• *Net Outstanding:* ₹${parseFloat(totals.totalBalance || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}\n`;
      msg += `• *Loading + Ice Charges:* ₹${(parseFloat(totals.totalHamali || 0) + parseFloat(totals.totalIce || 0)).toLocaleString('en-IN', { minimumFractionDigits: 2 })}\n`;
      msg += `--------------------------------\n`;
      msg += `_Generated from Fish Shop Mumbai Online System_`;
      return msg;
    }

    if (reportType === 'purchase_report') {
      const totals = reportData.totals || {};
      let msg = `📉 *PURCHASE SUMMARY REPORT*\n`;
      msg += `🏢 *${firmName}*\n`;
      msg += `📅 *Period:* ${fromDate} to ${toDate}\n`;
      msg += `--------------------------------\n`;
      msg += `• *Total Purchases Value:* ₹${parseFloat(totals.totalGrand || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}\n`;
      msg += `• *Items Subtotal:* ₹${parseFloat(totals.totalSubtotal || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}\n`;
      msg += `• *Total Discounts:* ₹${parseFloat(totals.totalDiscount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}\n`;
      msg += `--------------------------------\n`;
      msg += `_Generated from Fish Shop Mumbai Online System_`;
      return msg;
    }

    if (reportType === 'stock_report') {
      const items = reportData.stock || [];
      const totalQty = items.reduce((sum, i) => sum + (parseFloat(i.currentStockQty) || 0), 0);
      const totalVal = items.reduce((sum, i) => sum + ((parseFloat(i.currentStockQty) || 0) * (parseFloat(i.PRate) || 0)), 0);

      let msg = `📦 *LIVE INVENTORY / STOCK REPORT*\n`;
      msg += `🏢 *${firmName}*\n`;
      msg += `📅 *Date:* ${formatDate(new Date())}\n`;
      msg += `--------------------------------\n`;
      msg += `• *Total Product Varieties:* ${items.length} items\n`;
      msg += `• *Total Live Stock:* ${totalQty.toFixed(2)} KG\n`;
      msg += `• *Total Valuation (@ PRate):* ₹${totalVal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}\n`;
      msg += `--------------------------------\n`;
      msg += `_Generated from Fish Shop Mumbai Online System_`;
      return msg;
    }

    return `📊 *${title}*\n🏢 *${firmName}*\n📅 *Date:* ${formatDate(new Date())}`;
  };

  const handleSendWhatsAppWeb = () => {
    let cleanMobile = (targetMobile || '').replace(/\D/g, '');
    if (cleanMobile.length === 10) cleanMobile = '91' + cleanMobile;

    const text = buildWhatsAppMessage();
    const url = cleanMobile
      ? `https://api.whatsapp.com/send?phone=${cleanMobile}&text=${encodeURIComponent(text)}`
      : `https://api.whatsapp.com/send?text=${encodeURIComponent(text)}`;
    window.open(url, '_blank');
  };

  const handleSendViaBusinessApi = async () => {
    if (!targetMobile) {
      alert('Please enter a valid WhatsApp mobile number.');
      return;
    }

    setSendingApi(true);
    setApiStatusMsg('');
    try {
      const partyName = reportData.account?.AccountName || reportData.partyName || title;
      const saleDate = reportData.fromDate ? `${formatDate(reportData.fromDate)} to ${formatDate(reportData.toDate)}` : formatDate(new Date());
      const firmInfo = reportData.firmName || 'PUSHPAI SEA FOOD(ALIBAG)';
      const messageText = buildWhatsAppMessage();

      const payload = {
        phone: targetMobile,
        invoiceNo: reportType.toUpperCase(),
        type: reportType,
        partyName,
        saleDate,
        firmInfo,
        grandTotal: reportData.closingBalance || reportData.totals?.totalGrand || '0.00',
        messageText,
        ...reportData
      };

      const res = await axiosClient.post('/whatsapp/send-invoice', payload);

      if (res.success) {
        setApiStatusMsg('✅ Report & PDF sent via WhatsApp Business API successfully!');
        setTimeout(() => setApiStatusMsg(''), 6000);
      }
    } catch (err) {
      console.error('WhatsApp Report API Error:', err);
      const errMsg = err.message || 'Failed to send report via WhatsApp Business API.';
      setApiStatusMsg(`⚠️ ${errMsg}`);
      if (window.confirm(`${errMsg}\n\nWould you like to open WhatsApp Web instead?`)) {
        handleSendWhatsAppWeb();
      }
    } finally {
      setSendingApi(false);
    }
  };

  const handleCopyText = () => {
    const text = buildWhatsAppMessage();
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  return (
    <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(15, 23, 42, 0.75)', zIndex: 1060 }}>
      <div className="modal-dialog modal-lg modal-dialog-centered">
        <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '14px', overflow: 'hidden' }}>
          
          {/* Header Bar */}
          <div className="modal-header bg-dark text-white px-4 py-3 d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center gap-2">
              <span className="badge bg-success text-uppercase px-2 py-1">
                <i className="bi bi-whatsapp me-1"></i> WhatsApp Sharing
              </span>
              <h5 className="modal-title mb-0 fw-semibold fs-6">
                Share {title} via WhatsApp
              </h5>
            </div>
            <button type="button" className="btn-close btn-close-white" onClick={onClose} aria-label="Close"></button>
          </div>

          {/* Modal Body */}
          <div className="modal-body p-4 bg-light">
            <div className="card shadow-sm border-0 mb-3">
              <div className="card-body p-3">
                <label className="form-label small fw-semibold text-muted">
                  <i className="bi bi-telephone-fill text-success me-1"></i> Recipient WhatsApp Mobile Number *
                </label>
                <div className="input-group mb-2">
                  <span className="input-group-text bg-white fw-bold text-muted">+91</span>
                  <input
                    type="text"
                    className="form-control form-control-lg fw-bold"
                    placeholder="Enter 10-digit mobile number"
                    value={targetMobile}
                    onChange={(e) => setTargetMobile(e.target.value)}
                  />
                  <button
                    className="btn btn-success fw-semibold d-flex align-items-center gap-1 px-3"
                    onClick={handleSendWhatsAppWeb}
                  >
                    <i className="bi bi-whatsapp"></i> WhatsApp Web
                  </button>
                  <button
                    className="btn btn-primary fw-semibold d-flex align-items-center gap-1 px-3"
                    onClick={handleSendViaBusinessApi}
                    disabled={sendingApi}
                  >
                    {sendingApi ? (
                      <>
                        <span className="spinner-border spinner-border-sm" role="status"></span>
                        Sending...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-send-check-fill"></i> Send via API
                      </>
                    )}
                  </button>
                </div>
                {apiStatusMsg && (
                  <div className="small fw-semibold text-truncate mt-1" style={{ fontSize: '0.85rem' }}>
                    {apiStatusMsg}
                  </div>
                )}
              </div>
            </div>

            {/* Message Preview */}
            <div className="card shadow-sm border-0">
              <div className="card-header bg-white py-2 d-flex justify-content-between align-items-center">
                <span className="fw-semibold small text-success">
                  <i className="bi bi-chat-left-text-fill me-1"></i> WhatsApp Message Summary:
                </span>
                <button className="btn btn-sm btn-outline-primary" onClick={handleCopyText}>
                  <i className={`bi ${copied ? 'bi-check-lg' : 'bi-clipboard'} me-1`}></i>
                  {copied ? 'Copied!' : 'Copy Text'}
                </button>
              </div>
              <div className="card-body p-3">
                <textarea
                  className="form-control font-monospace small bg-light"
                  rows={8}
                  readOnly
                  value={buildWhatsAppMessage()}
                />
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="modal-footer bg-white px-4 py-2 d-flex justify-content-between">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Close
            </button>
            <div className="d-flex gap-2">
              <button
                type="button"
                className="btn btn-success d-flex align-items-center gap-1"
                onClick={handleSendWhatsAppWeb}
              >
                <i className="bi bi-whatsapp"></i> Open WhatsApp Web
              </button>
              <button
                type="button"
                className="btn btn-primary d-flex align-items-center gap-1"
                onClick={handleSendViaBusinessApi}
                disabled={sendingApi}
              >
                <i className="bi bi-send-check-fill"></i> Send via API
              </button>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default ReportWhatsAppModal;
